import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import {
  useGetPublicGroupsQuery,
  useRequestCodeMutation,
  useVerifyCodeMutation,
  useSubscribeMutation,
  useUnsubscribeMutation,
  useGetMyGroupsQuery,
  // useLazyGetPushPublicKeyQuery,
  // useAddPushSubscriptionMutation,
} from '../../../api/subscribeApi';
import { selectIsSubscriberAuthenticated } from '../../../slices/subscriberAuthSlice';
import TermsGate from '../../../components/TermsGate/TermsGate';
import getSubdomain from '../../../utils/getSubdomain';
import { errMsg } from '../../../utils/errMsg';
// import { subscribeToPush } from '../../../utils/webPush';
import buttons from '../../../styles/shared/buttons.module.css';
import card from '../../../styles/shared/publicCard.module.css';
import styles from './SubscribePage.module.css';

const STEP = {
  EMAIL: 'email',
  CODE: 'code',
  ALREADY_SUBSCRIBED: 'already_subscribed',
  LOCATION: 'location',
  TERMS: 'terms',
  DONE: 'done',
  UNSUBSCRIBED: 'unsubscribed',
};

function nextStepAfterAuth(group) {
  return group?.geolocation_required ? STEP.LOCATION : STEP.TERMS;
}

export default function SubscribePage() {
  const { organizationSlug: slugFromPath, groupId } = useParams();
  const subdomainSlug = getSubdomain();
  const organizationSlug = slugFromPath ?? subdomainSlug;
  const backToGroupsPath = subdomainSlug ? '/' : `/o/${organizationSlug}`;

  const { data: groups, isLoading: loadingGroup } =
    useGetPublicGroupsQuery(organizationSlug);
  const group = useMemo(
    () => groups?.find((g) => g.id === groupId),
    [groups, groupId],
  );

  const isAuthed = useSelector(selectIsSubscriberAuthenticated);
  const {
    data: myGroups,
    isSuccess: myGroupsLoaded,
    isError: myGroupsError,
  } = useGetMyGroupsQuery(organizationSlug, { skip: !isAuthed });

  const [step, setStep] = useState(() => (isAuthed ? null : STEP.EMAIL));
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [subscriberId, setSubscriberId] = useState(null);
  const [channel, setChannel] = useState('email');
  const [coords, setCoords] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [locationRequesting, setLocationRequesting] = useState(false);
  // const [pushError, setPushError] = useState(null);
  // const [pushBusy, setPushBusy] = useState(false);

  const [requestCode, requestState] = useRequestCodeMutation();
  const [verifyCode, verifyState] = useVerifyCodeMutation();
  const [subscribe, subscribeState] = useSubscribeMutation();
  const [unsubscribe, unsubscribeState] = useUnsubscribeMutation();
  // const [getPushPublicKey] = useLazyGetPushPublicKeyQuery();
  // const [addPushSubscription] = useAddPushSubscriptionMutation();

  useEffect(() => {
    const available = group?.channels?.filter((c) => c !== 'push') ?? [];
    if (available.length === 1) setChannel(available[0]);
  }, [group]);

  useEffect(() => {
    if (!isAuthed || step !== null) return;
    if (myGroupsError) {
      setStep(STEP.EMAIL);
      return;
    }
    if (!myGroupsLoaded || !group) return;
    const existing = myGroups.groups.find(({ group: g }) => g.id === groupId);
    if (existing) {
      setChannel(existing.subscription.channel);
      setStep(STEP.ALREADY_SUBSCRIBED);
    } else {
      setStep(nextStepAfterAuth(group));
    }
  }, [isAuthed, step, myGroupsLoaded, myGroupsError, myGroups, groupId, group]);

  function handleRequestCode(e) {
    e.preventDefault();
    requestCode({ organizationSlug, email })
      .unwrap()
      .then((res) => {
        setSubscriberId(res.subscriber_id);
        if (res.dev_code) setCode(String(res.dev_code));
        setStep(STEP.CODE);
      })
      .catch(() => {});
  }

  function handleVerify(e) {
    e.preventDefault();
    verifyCode({ organizationSlug, subscriberId, code })
      .unwrap()
      .then((res) => {
        const existing = (res.subscriptions ?? []).find((s) => s.group_id === groupId);
        if (existing) {
          setChannel(existing.channel);
          setStep(STEP.ALREADY_SUBSCRIBED);
        } else {
          setStep(nextStepAfterAuth(group));
        }
      })
      .catch(() => {});
  }

  function handleShareLocation() {
    if (!navigator.geolocation) {
      setLocationError("Your browser doesn't support location sharing.");
      return;
    }
    setLocationError(null);
    setLocationRequesting(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
        setLocationRequesting(false);
        setStep(STEP.TERMS);
      },
      () => {
        setLocationError('Location permission was denied. This group requires it to subscribe.');
        setLocationRequesting(false);
      },
    );
  }

  async function handleAgree() {
    // if (channel === 'push') {
    //   setPushBusy(true);
    //   try {
    //     await subscribeToPush({ organizationSlug, getPushPublicKey, addPushSubscription });
    //   } catch (err) {
    //     setPushError(err.message || 'Could not enable push notifications.');
    //     setPushBusy(false);
    //     return;
    //   }
    //   setPushBusy(false);
    // }

    subscribe({
      organizationSlug,
      subscriberId,
      groupId,
      channel,
      latitude: coords?.lat,
      longitude: coords?.lng,
    })
      .unwrap()
      .then(() => setStep(STEP.DONE))
      .catch(() => {});
  }

  function handleUnsubscribe() {
    unsubscribe({ organizationSlug, subscriberId, groupId, channel })
      .unwrap()
      .then(() => setStep(STEP.UNSUBSCRIBED))
      .catch(() => {});
  }

  if (loadingGroup) return <p className={card.status}>Loading…</p>;
  if (!group)
    return (
      <div className={card.wrapper}>
        <Card className={`${card.card} ${styles.card}`}>
          <p className={card.status}>This group isn&apos;t available.</p>
          <Link to={backToGroupsPath} className={styles.backLink}>
            ← All groups
          </Link>
        </Card>
      </div>
    );
  if (step === null) return <p className={card.status}>Loading…</p>;

  const channels = (group.channels ?? ['email']).filter((c) => c !== 'push');

  return (
    <div className={card.wrapper}>
      <Card title={`Subscribe to ${group.name}`} className={`${card.card} ${styles.card}`}>
        <Link to={backToGroupsPath} className={styles.backLink}>
          ← All groups
        </Link>

        {step === STEP.EMAIL && (
          <form onSubmit={handleRequestCode} className={styles.panel} noValidate>
            <p className={styles.panelHint}>
              Enter your work or school email to get a verification code.
            </p>
            <InputText
              type="email"
              className={styles.panelInput}
              placeholder="you@campus.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              invalid={requestState.isError}
              required
            />
            <Button
              type="submit"
              label={requestState.isLoading ? 'Sending…' : 'Send code'}
              loading={requestState.isLoading}
              className={buttons.accentBtn}
            />
            {requestState.isError && (
              <div className={styles.errorText}>
                {errMsg(
                  requestState.error,
                  'Could not send a code. Is your email from this organization?',
                )}
              </div>
            )}
          </form>
        )}

        {step === STEP.CODE && (
          <form onSubmit={handleVerify} className={styles.panel}>
            <p className={styles.panelHint}>
              Enter the 6-digit code we emailed to {email}.
            </p>
            <InputText
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              className={styles.panelInput}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
            <Button
              type="submit"
              label={verifyState.isLoading ? 'Verifying…' : 'Verify'}
              loading={verifyState.isLoading}
              className={buttons.accentBtn}
            />
            {verifyState.isError && (
              <div className={styles.errorText}>
                {errMsg(verifyState.error, 'That code is invalid or expired.')}
              </div>
            )}
          </form>
        )}

        {step === STEP.LOCATION && (
          <div className={styles.panel}>
            <p className={styles.panelHint}>
              {group.name} only notifies subscribers near its events. Share your location to
              continue — we only store it to check distance when an alert goes out.
            </p>
            <Button
              type="button"
              label={locationRequesting ? 'Requesting…' : 'Share my location'}
              loading={locationRequesting}
              className={buttons.accentBtn}
              onClick={handleShareLocation}
            />
            {locationError && <div className={styles.errorText}>{locationError}</div>}
          </div>
        )}

        {step === STEP.TERMS && (
          <div className={styles.panel}>
            {channels.length > 1 && (
              <div className="field">
                <label htmlFor="channel" className={styles.channelLabel}>
                  Notify me via
                </label>
                <Dropdown
                  inputId="channel"
                  value={channel}
                  onChange={(e) => setChannel(e.value)}
                  options={channels.map((c) => ({ label: c, value: c }))}
                  className="w-full"
                />
              </div>
            )}
            <p className={styles.panelHint}>Please review and accept the terms:</p>
            <TermsGate
              terms={group.terms}
              onAgree={handleAgree}
              isSubmitting={subscribeState.isLoading}
            />
            {subscribeState.isError && (
              <div className={styles.errorText}>
                {errMsg(subscribeState.error, 'Could not subscribe.')}
              </div>
            )}
          </div>
        )}

        {step === STEP.ALREADY_SUBSCRIBED && (
          <div className={styles.panel}>
            <h2 className={styles.doneTitle}>You&apos;re already subscribed</h2>
            <p className={styles.panelHint}>
              You&apos;re getting {channel} alerts for {group.name}.
            </p>
            <Button
              type="button"
              label={unsubscribeState.isLoading ? 'Unsubscribing…' : 'Unsubscribe from this group'}
              loading={unsubscribeState.isLoading}
              className={buttons.accentBtn}
              onClick={handleUnsubscribe}
            />
            {unsubscribeState.isError && (
              <div className={styles.errorText}>
                {errMsg(unsubscribeState.error, 'Could not unsubscribe.')}
              </div>
            )}
          </div>
        )}

        {step === STEP.DONE && (
          <div className={styles.panel}>
            <h2 className={styles.doneTitle}>You&apos;re subscribed 🎉</h2>
            <p className={styles.panelHint}>
              You&apos;ll get {channel} alerts for {group.name}.
            </p>
            <Button
              type="button"
              label={unsubscribeState.isLoading ? 'Unsubscribing…' : 'Unsubscribe from this group'}
              loading={unsubscribeState.isLoading}
              text
              onClick={handleUnsubscribe}
            />
            {unsubscribeState.isError && (
              <div className={styles.errorText}>
                {errMsg(unsubscribeState.error, 'Could not unsubscribe.')}
              </div>
            )}
            <Link to={backToGroupsPath} className={styles.backLink}>
              Subscribe to another group
            </Link>
          </div>
        )}

        {step === STEP.UNSUBSCRIBED && (
          <div className={styles.panel}>
            <h2 className={styles.doneTitle}>You&apos;re unsubscribed</h2>
            <p className={styles.panelHint}>
              You won&apos;t get any more {group.name} alerts on this channel.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
